import {
  isSymbolRoot,
  isSymbolInst,
} from '@packages/systems/core/utils/DomTypeUtils';
import {without} from 'lodash';
import type {Context} from '../types';
import {
  emptyEnhancedExtraContext,
  type EnhancedExtraContext,
} from '../ExtraContext';
import type {JSONNode} from '@packages/shared/types/JSONNode';

/**
 * This patch deletes symbol instances that point to a symbol root that doesn't exist (or is not a root)
 * It relies on having the symbol tree passed in as extraContext, if extraContent.symbols is not provided
 * it will just be needed = false and skip the patch.
 */

function isPatchNeeded(
  jsonNode: JSONNode,
  extraContext: EnhancedExtraContext = emptyEnhancedExtraContext
) {
  const {symbolNodeMap, pluginComponents} = extraContext;
  const {data = {}} = jsonNode;

  // if symbols are not passed in via extraContext just skip the patch
  if (!symbolNodeMap) {
    return false;
  }

  if (!isSymbolInst(data)) {
    return false;
  }

  const rootSymbolId = data.sym.inst;

  // See if a root symbol exists in the symbolTree
  const symbolNode = symbolNodeMap.get(rootSymbolId);
  const rootSymbolExists =
    (symbolNode && isSymbolRoot(symbolNode.data)) ||
    Boolean(
      pluginComponents &&
      pluginComponents.find(
        (_, [_pluginType, elName]: [any, any]) => rootSymbolId === elName
      )
    );

  return !rootSymbolExists;
}

export default (context: Context) => {
  const {
    node: {jsonNode},
    tree: {jsonNodes},
    extraContext,
  } = context;

  const patchNeeded = isPatchNeeded(jsonNode, extraContext);

  return {
    needed: patchNeeded,
    logData: {
      msg: 'Symbol Instance of Deleted Symbol',
      meta: {
        nodeId: jsonNode._id,
      },
    },
    apply: () => {
      const instanceId = jsonNode._id;
      // Remove the instance from its parent, if it has a parent (it should)
      const parent = jsonNodes.find((parentNode) => {
        return parentNode.children && parentNode.children.includes(instanceId);
      });

      if (parent) {
        parent.children = without(parent.children, instanceId);
      }
      // Delete the instance from the node tree.
      return [];
    },
  };
};
